export function phoneNumberIsValid(phone: string): boolean {
    const phoneRegEx = new RegExp('^[+]?[(]?[0-9]{3}[)]?[-s.]?[0-9]{3}[-s.]?[0-9]{4,6}$');

    return phoneRegEx.test(phone);
}

export function emailAddressIsValid(email: string): boolean {
    const emailRegEx = new RegExp('^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$');

    return emailRegEx.test(email);
}

/**
 * Verbatim copies of the two patterns ajv-formats uses for JSON Schema `format: "email"`. The surveys
 * service declares surveyContactMethods[].emailAddress as zod `.string().email()`, which
 * zodToJsonSchema emits as `format: "email"`, so these are the real acceptance criteria for anything
 * we put in a batch payload. `emailAddressIsValid` above is far looser than either, and an address it
 * wrongly accepts fails schema validation and 400s the ENTIRE batch request (taking up to 49
 * unrelated surveys down with it), while one we wrongly reject costs only that survey's email contact
 * method — so callers require BOTH patterns to pass.
 *
 * Verified 2026-07-28 against graphium-surveys apps/services/schema/survey.ts (identical on
 * origin/main and origin/develop): the service's effective pattern is fullFormats.email, because
 * @fastify/ajv-compiler v4 calls `require('ajv-formats')(ajv)` with no options and ajv-formats
 * defaults to `mode: 'full'` (formatsPlugin: `opts.mode === "fast" ? fastFormats : fullFormats`).
 * Requiring both is therefore slightly stricter than the service on exactly one axis — domain labels
 * over 63 characters, which full accepts and fast does not — and is kept that way deliberately so a
 * future change to the registered mode cannot widen what we send. The two patterns are byte-identical
 * between ajv-formats 2.1.1 (this repo) and 3.0.1 (the service).
 *
 * Source: node_modules/ajv-formats/dist/formats.js — fastFormats.email / fullFormats.email.
 */
export const AJV_EMAIL_FAST =
    /^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*$/i;
export const AJV_EMAIL_FULL =
    /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;

/** Every character ajv's email format can accept. Anything else is reported as an offender. */
const AJV_EMAIL_CHARSET = /[A-Za-z0-9.!#$%&'*+/=?^_`{|}~@-]/;

/**
 * Render an address as a safe-to-log skeleton: every alphanumeric becomes 'x', punctuation and
 * structure are preserved verbatim, and anything outside printable ASCII becomes a \uXXXX escape.
 *
 * This is deliberately not the address. Email addresses are patient PII and must not reach the log
 * pipeline, but every defect we need to diagnose lives in the structure rather than the letters:
 *   'a@b.com.'          -> "x@x.xxx."          (trailing dot)
 *   'a@b.com' + U+200B  -> "x@x.xxx​"     (zero-width space)
 *   'a@my_domain.com'   -> "x@xx_xxxxxx.xxx"   (underscore in domain)
 *   Cyrillic О in .COM  -> "x@x.xОx"      (homoglyph)
 *
 * Note that JSON.stringify would NOT be sufficient even for the escaping half: it escapes only
 * control characters below 0x20, quotes, and backslashes, so a zero-width space or a homoglyph
 * passes through untouched and stays invisible — the exact failure this diagnostic exists to expose.
 */
function maskedShapeOf(value: string): string {
    let shape = '';
    for (const character of value) {
        const codePoint = character.codePointAt(0) ?? 0;
        if (/[A-Za-z0-9]/.test(character)) {
            shape += 'x';
        } else if (codePoint >= 0x20 && codePoint <= 0x7e) {
            shape += character;
        } else if (codePoint > 0xffff) {
            shape += `\\u{${codePoint.toString(16).toUpperCase()}}`;
        } else {
            shape += `\\u${codePoint.toString(16).toUpperCase().padStart(4, '0')}`;
        }
    }
    return shape;
}

export interface EmailDiagnostic {
    /**
     * Safe-to-log skeleton of the RAW address — alphanumerics masked, structure and invisible
     * characters preserved. Never contains the address itself; see maskedShapeOf.
     */
    maskedShape: string;
    /** Of the raw value, so a stray character shows up as a length that does not match the shape. */
    chars: number;
    bytes: number;
    /** Of the raw (trimmed) value — what the old loose guard would have decided. */
    passesLoose: boolean;
    /** Of the NORMALIZED value, since that is what we would actually send. */
    passesAjvFast: boolean;
    passesAjvFull: boolean;
    /** What emailAddressIsAcceptedBySurveysService returns: safe to send. */
    accepted: boolean;
    /**
     * The old loose guard would have sent it and the service would have rejected it — precisely the
     * class of address that 400s a whole batch and stalls a migration job. False once normalization
     * recovers the address, because then there is nothing left to fail.
     */
    wouldFailService: boolean;
    /** Per-character detail of the raw value, e.g. 'pos 22 U+200B "​"'. Empty when the cause is structural. */
    offenders: string[];
}

/**
 * Test an address against both our loose guard and ajv's real patterns, and describe the result in a
 * form that survives a log pipeline WITHOUT carrying the address itself, which is patient PII.
 * `maskedShape` is the important field: it turns otherwise-invisible characters (zero-width spaces,
 * homoglyphs, stray control codes) and easily-missed ones (a trailing dot) into something readable
 * in Datadog, while masking the letters and digits that identify the patient.
 *
 * `offenders` reports disallowed characters only. A structurally invalid address made entirely of
 * legal characters — 'a@b.com.', 'a..b@c.com', 'a@my_domain.com' — yields an empty list, so read the
 * three pass* booleans for the verdict and use `maskedShape` to see why.
 */
export function describeEmailAddress(rawEmail: string): EmailDiagnostic {
    const trimmed = rawEmail.trim();
    const normalized = normalizeEmailAddress(rawEmail);
    const passesLoose = emailAddressIsValid(trimmed);
    // Judge the ajv patterns on the normalized value: that is what we would put in the payload, so
    // anything else would report a failure we are not actually going to cause.
    const passesAjvFast = AJV_EMAIL_FAST.test(normalized);
    const passesAjvFull = AJV_EMAIL_FULL.test(normalized);
    const accepted = emailAddressIsAcceptedBySurveysService(rawEmail);

    // Offenders are reported against the raw value so the original defect stays visible even when
    // normalization goes on to recover the address.
    const offenders: string[] = [];
    for (let i = 0; i < trimmed.length; i += 1) {
        const character = trimmed[i];
        if (!AJV_EMAIL_CHARSET.test(character)) {
            const codePoint = (trimmed.codePointAt(i) ?? 0).toString(16).toUpperCase().padStart(4, '0');
            offenders.push(`pos ${i + 1} U+${codePoint} ${JSON.stringify(character)}`);
        }
    }

    return {
        maskedShape: maskedShapeOf(rawEmail),
        chars: rawEmail.length,
        bytes: Buffer.byteLength(rawEmail, 'utf8'),
        passesLoose,
        passesAjvFast,
        passesAjvFull,
        accepted,
        wouldFailService: passesLoose && !accepted,
        offenders,
    };
}

/** Zero-width space, ZWNJ, ZWJ, and the BOM — never part of an address, invisible in every client. */
const ZERO_WIDTH_CHARACTERS = /[​-‍﻿]/g;

/**
 * Clean an address by DELETING characters that cannot be part of any valid address, and nothing else.
 *
 * This is deliberately deletion-only. It is provably conservative: no address ajv accepts contains a
 * zero-width character, surrounding whitespace, or a trailing '.,;:' (full-mode domains must end in
 * [a-z0-9]), so normalization can only ever turn an invalid address into a valid one — it can never
 * alter one that was already acceptable. `normalizeIsLossless` in the spec pins that property.
 *
 * Substitution is explicitly NOT done here, because it is guessing rather than cleaning. Given
 * 'user@mail/co.com', deleting the slash yields 'user@mailco.com' and replacing it with a dot yields
 * 'user@mail.co.com' — both valid, both different, and neither knowably correct. Picking one would
 * send patient survey links to whichever third party owns the domain we guessed. Homoglyphs (Cyrillic
 * О for Latin O) are the same class of guess. Those addresses are dropped and reported instead, so a
 * human can repair the source record deliberately.
 */
export function normalizeEmailAddress(email: string): string {
    return email.replace(ZERO_WIDTH_CHARACTERS, '').trim().replace(/[.,;:]+$/, '');
}

/**
 * True only when the surveys service will accept the address (after lossless normalization) under
 * either ajv-formats mode. This is the guard to use before putting an address in a payload.
 */
export function emailAddressIsAcceptedBySurveysService(email: string | null | undefined): boolean {
    if (!email) {
        return false;
    }
    const normalized = normalizeEmailAddress(email);
    if (normalized.length > 254) {
        return false; // RFC 5321 cap; neither ajv pattern bounds total length
    }
    return AJV_EMAIL_FAST.test(normalized) && AJV_EMAIL_FULL.test(normalized);
}
